import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import block from 'bem-cn'
import dayjs from 'dayjs'

import { ROUTE_URLS } from 'router/routes'

import { BonusPromotionStatus } from 'shared/models/Promotion'

import { SlotsGameType } from 'modules/SlotsRoot/types/interfaces/Slots/Slots'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { setTab } from 'features/bonusSystem/actions'
import { selectBonusPromotionsPolling } from 'features/bonusSystem/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { TopBannerHoc } from 'components/HOC/TopBannerHoc/TopBannerHoc'
import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import Button from 'components/UIKit/Button/Button'

import {
  activePromotionStatuses,
  bonusPromotionStatusWeights,
  takeYourBonusStatuses,
} from './BonusPromotionNotification.utils'

import './BonusPromotionNotification.scss'

const b = block('bonus-promotion-notification')

export const BonusPromotionNotification = () => {
  const history = useHistory()
  const locale = useSelector(selectLocaleDict, shallowEqual).bonuses
  const { pushNewModal } = useModalContext()
  const promotions = useSelector(selectBonusPromotionsPolling, shallowEqual)
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const dispatch = useDispatch()
  const [hasLoadedPromotions, setHasLoadedPromotions] = useState(false)
  const [isClosed, setIsClosed] = useState(false)

  useEffect(() => {
    if (isAuth && promotions.length > 0) {
      setHasLoadedPromotions(true)
    }
  }, [isAuth, promotions])

  const activePromotion = useMemo(
    () => promotions.find(promotion => activePromotionStatuses.includes(promotion.status)),
    [promotions],
  )

  const takeBonus = useMemo(
    () => promotions.find(promotion => takeYourBonusStatuses.includes(promotion.status)),
    [promotions],
  )

  const activePromotionWeight = useMemo(
    () => (activePromotion ? bonusPromotionStatusWeights[activePromotion.status] : 0),
    [activePromotion],
  )

  const takeBonusWeight = useMemo(() => (takeBonus ? bonusPromotionStatusWeights[takeBonus.status] : 0), [takeBonus])

  const currentDate = dayjs()

  const allPromotionsIsExpired = useMemo(
    () => promotions?.every(promo => currentDate.diff(promo.expirationDate) >= 0),
    [promotions, currentDate],
  )

  const handleCloseBanner = useCallback(() => setIsClosed(true), [])

  const shouldShowNotification = useMemo(
    () =>
      isAuth && hasLoadedPromotions && !allPromotionsIsExpired && !isClosed && Boolean(activePromotion || takeBonus),
    [isAuth, hasLoadedPromotions, allPromotionsIsExpired, activePromotion, takeBonus, isClosed],
  )

  const handleButtonClick = useCallback(
    (route: string, isModal?: boolean) => {
      if (isModal) {
        pushNewModal(route)()
      } else {
        history.push(route)
      }
    },
    [history, pushNewModal],
  )

  if (!shouldShowNotification) return null

  if (activePromotion && takeBonusWeight < activePromotionWeight) {
    return activePromotion!.freeSpinsCount > 0 ? (
      <TopBannerHoc
        allPromotionsIsExpired={allPromotionsIsExpired}
        isShowNotification
        onCloseBanner={handleCloseBanner}
        needAuth>
        <div className={b({ isFreeSpins: true })}>
          <div className={b('text')}>{locale.clickForPlayFreeSpins}</div>

          <div className={b('button-wrapper')}>
            <Button
              text={locale.openGames}
              callback={() => handleButtonClick(ROUTE_URLS.SLOTS_GAMETYPE(SlotsGameType.freeSpin))}
            />
          </div>
        </div>
      </TopBannerHoc>
    ) : (
      <TopBannerHoc
        allPromotionsIsExpired={allPromotionsIsExpired}
        isShowNotification
        onCloseBanner={handleCloseBanner}
        needAuth>
        <div className={b({ isWager: true })}>
          <div className={b('text')}>
            <span>
              {locale.wagerTextPromotion(
                Number(activePromotion.promotionGrandAmount.toFixed(2)),
                activePromotion.wagerCurrency,
              )}
            </span>
          </div>

          <div className={b('button-wrapper')}>
            <Button
              text={locale.openGames}
              callback={() => handleButtonClick(ROUTE_URLS.SLOTS_GAMETYPE(SlotsGameType.wagering))}
            />
          </div>
        </div>
      </TopBannerHoc>
    )
  }

  if (takeBonus) {
    return (
      <TopBannerHoc
        allPromotionsIsExpired={allPromotionsIsExpired}
        isShowNotification
        onCloseBanner={handleCloseBanner}
        needAuth>
        <div className={b({ isTakeBonus: true })}>
          <div className={b('text')}>{locale.takeYourBonus}</div>
          <div className={b('button-wrapper')}>
            <Button
              text={locale.takeBonus}
              callback={() => {
                dispatch(setTab(BonusPromotionStatus.available))
                const baseUrl = ROUTE_URLS.PROFILE_PROMOTIONS()
                const params = `?openedModals=%2Fpromotions&status=${BonusPromotionStatus.available}`
                const url = `${baseUrl}${params}`
                handleButtonClick(url)
              }}
            />
          </div>
        </div>
      </TopBannerHoc>
    )
  }

  return null
}
